import * as Sentry from '@sentry/nextjs';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useEffectEvent } from './useEffectEvent';
import { delay } from '../lib/utils';

const useHTML5VideoSeekDisabler = (
  ref: RefObject<HTMLDivElement>,
  startTime = 0,
  enabled = true,
) => {
  const video = ref.current?.getElementsByTagName('video')[0];
  const timeTrackingRef = useRef<{
    watchedTime: number;
    currentTime: number;
    lastUpdated: 'currentTime' | 'watchedTime';
    isSeekDisablePaused: boolean;
  }>({
    watchedTime: startTime || 0,
    currentTime: startTime || 0,
    lastUpdated: 'currentTime',
    isSeekDisablePaused: false,
  });
  const setVideoTime = useEffectEvent(async (videoTime: number) => {
    try {
      if (video) {
        video.pause();
        video.currentTime = videoTime;
        await video.play();
      }
    } catch (err) {
      console.log('error', err);
    }
  });
  const onTimeUpdate = useEffectEvent(() => {
    if (video) {
      if (video.playbackRate != 1) video.playbackRate = 1;
      if (video.readyState < 3) return;
      if (!video.seeking) {
        if (video.currentTime > timeTrackingRef.current.watchedTime) {
          timeTrackingRef.current.watchedTime = video.currentTime;
          timeTrackingRef.current.lastUpdated = 'watchedTime';
        }
        //tracking time updated after user rewinds
        else {
          if (timeTrackingRef.current.isSeekDisablePaused) {
            timeTrackingRef.current.lastUpdated = 'currentTime';
            timeTrackingRef.current.currentTime = video.currentTime;
            timeTrackingRef.current.watchedTime = video.currentTime;
          } else {
            let delta = timeTrackingRef.current.watchedTime - video.currentTime;
            if (delta > 30) delta = 30;
            if (
              delta > 0 &&
              timeTrackingRef.current.currentTime - video.currentTime > 1
            ) {
              Sentry.captureMessage('CORRECTING_VIDEO_TIME_onTimeUpdate', {
                tags: {
                  timetrackingCurrentTime: timeTrackingRef.current.currentTime,
                  timeTrackingWatchedTime: timeTrackingRef.current.watchedTime,
                  timeTrackingLastUpdated: timeTrackingRef.current.lastUpdated,
                  timeTrackingStatus:
                    timeTrackingRef.current.isSeekDisablePaused,
                  videotimeInHTML5Player: video.currentTime,
                  delta: delta,
                  timeToCorrect: timeTrackingRef.current.currentTime,
                  caller: 'onTimeUpdate',
                  html5PlayerState: video.readyState,
                  isSeeking: video.seeking,
                  startTime,
                },
              });
              timeTrackingRef.current.currentTime =
                timeTrackingRef.current.watchedTime - delta;
              setVideoTime(timeTrackingRef.current.currentTime);
              timeTrackingRef.current.lastUpdated = 'currentTime';
            }
          }
        }
      }
    }
  });

  const onSeek = useEffectEvent(() => {
    if (video && !timeTrackingRef.current.isSeekDisablePaused) {
      var delta = video.currentTime - timeTrackingRef.current.watchedTime;
      if (isNaN(delta)) return;
      delta = Math.round(delta);
      if (delta >= 2) {
        Sentry.captureMessage('CORRECTING_VIDEO_TIME_onSeek-forward', {
          tags: {
            timetrackingCurrentTime: timeTrackingRef.current.currentTime,
            timeTrackingWatchedTime: timeTrackingRef.current.watchedTime,
            timeTrackingLastUpdated: timeTrackingRef.current.lastUpdated,
            timeTrackingStatus: timeTrackingRef.current.isSeekDisablePaused,
            videotimeInHTML5Player: video.currentTime,
            delta: delta,
            timeToCorrect:
              timeTrackingRef.current[timeTrackingRef.current.lastUpdated],
            caller: 'onSeek-forward',
            html5PlayerState: video.readyState,
            isSeeking: video.seeking,
            startTime,
          },
        });

        //play back from where the user started seeking after rewind or without rewind
        setVideoTime(
          timeTrackingRef.current[timeTrackingRef.current.lastUpdated],
        );
      } else if (delta < -30) {
        Sentry.captureMessage('CORRECTING_VIDEO_TIME_onSeek-rewind', {
          tags: {
            timetrackingCurrentTime: timeTrackingRef.current.currentTime,
            timeTrackingWatchedTime: timeTrackingRef.current.watchedTime,
            timeTrackingLastUpdated: timeTrackingRef.current.lastUpdated,
            timeTrackingStatus: timeTrackingRef.current.isSeekDisablePaused,
            videotimeInHTML5Player: video.currentTime,
            delta: delta,
            timeToCorrect: timeTrackingRef.current.watchedTime - 30,
            caller: 'onSeek-rewind',
            html5PlayerState: video.readyState,
            isSeeking: video.seeking,
            startTime,
          },
        });
        setVideoTime(timeTrackingRef.current.watchedTime - 30);
      }
    }
  });
  useEffect(() => {
    if (video && enabled) {
      video.addEventListener('timeupdate', onTimeUpdate);
      // prevent user from seeking
      video.addEventListener('seeking', onSeek);
      return () => {
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('seeking', onSeek);
      };
    }
  }, [video, enabled]);
  const startStopSeekDisable = useCallback(async (startStop: boolean) => {
    if (!startStop) await delay(500);
    timeTrackingRef.current.isSeekDisablePaused = startStop;
  }, []);
  const adjustVideoTime = useCallback((time: number) => {
    timeTrackingRef.current.currentTime = time;
    timeTrackingRef.current.watchedTime = time;
    timeTrackingRef.current.lastUpdated = 'watchedTime';
  }, []);
  return { adjustVideoTime, startStopSeekDisable };
};

export default useHTML5VideoSeekDisabler;
